import { useState, useEffect } from 'react';
import { Clock, Shield, Info } from 'lucide-react';

interface BuyRestrictionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (minutes: number) => void;
  currentMinutes: number;
  isLoading?: boolean;
}

/**
 * Calculate cutoff time in PST and ET
 */
function calculateCutoffTimes(minutes: number): { pst: string; et: string } | null {
  if (minutes === 0) return null;

  // Market close: 1:00 PM PST = 13:00 = 780 minutes from midnight
  const marketCloseMinutes = 13 * 60; // 780
  const cutoffMinutes = marketCloseMinutes - minutes;
  
  const hours = Math.floor(cutoffMinutes / 60);
  const mins = cutoffMinutes % 60;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
  
  const pst = `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm} PST`;
  
  // Convert to ET (PST + 3 hours)
  const etMinutes = cutoffMinutes + 180; // Add 3 hours
  const etHours = Math.floor(etMinutes / 60);
  const etMins = etMinutes % 60;
  const etAmpm = etHours >= 12 ? 'PM' : 'AM';
  const etDisplayHours = etHours > 12 ? etHours - 12 : (etHours === 0 ? 12 : etHours);
  
  const et = `${etDisplayHours}:${etMins.toString().padStart(2, '0')} ${etAmpm} ET`;
  
  return { pst, et };
}

/**
 * Format minutes into human-readable label
 */
function formatLabel(minutes: number): string {
  if (minutes === 0) return 'Never';
  if (minutes < 60) return `${minutes}m before`;
  const hours = minutes / 60;
  if (hours % 1 === 0) return `${hours}h before`;
  return `${hours}h before`;
}

/**
 * Generate dropdown options (0, 30, 60, ..., 390)
 */
function generateOptions(): { value: number; label: string }[] {
  const options = [{ value: 0, label: 'Never' }];
  
  // Generate 30-minute increments up to 390 minutes (6.5 hours)
  for (let minutes = 30; minutes <= 390; minutes += 30) {
    options.push({
      value: minutes,
      label: formatLabel(minutes),
    });
  }
  
  return options;
}

export function BuyRestrictionDialog({
  isOpen,
  onClose,
  onConfirm,
  currentMinutes,
  isLoading = false,
}: BuyRestrictionDialogProps) {
  const [selectedMinutes, setSelectedMinutes] = useState(currentMinutes);
  const options = generateOptions();

  // Update selected minutes when dialog opens with new current value
  useEffect(() => {
    if (isOpen) {
      setSelectedMinutes(currentMinutes);
    }
  }, [isOpen, currentMinutes]);

  if (!isOpen) return null;

  const cutoffTimes = calculateCutoffTimes(selectedMinutes);

  const handleConfirm = () => {
    onConfirm(selectedMinutes);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-6">
        {/* Title */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Buy Time Restriction
          </h2>
        </div>

        {/* Info Section - Robinhood-inspired */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 space-y-3 border border-blue-100 dark:border-blue-800">
          {/* Market Close Info */}
          <div className="flex items-start space-x-3">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Market Close</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                1:00 PM PST <span className="text-base font-normal text-gray-600 dark:text-gray-400">(4:00 PM ET)</span>
              </div>
            </div>
          </div>

          {/* Buy Cutoff Info - Only show if not "Never" */}
          {cutoffTimes && (
            <div className="flex items-start space-x-3 pt-3 border-t border-blue-200 dark:border-blue-700">
              <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Buy Cutoff</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {cutoffTimes.pst} <span className="text-base font-normal text-gray-600 dark:text-gray-400">({cutoffTimes.et})</span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Buys after this time will be rejected
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Dropdown */}
        <div>
          <label htmlFor="buyRestriction" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Stop accepting webhook buys
          </label>
          <select
            id="buyRestriction"
            value={selectedMinutes}
            onChange={(e) => setSelectedMinutes(Number(e.target.value))}
            className="w-full pl-4 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base text-gray-900 dark:text-white bg-white dark:bg-gray-700 appearance-none"
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Warning Text */}
        <div className="flex items-start space-x-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-600 dark:text-gray-400">
            This only affects webhook-triggered buys. Manual trades and SELL orders are not restricted.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex space-x-3 pt-2">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Updating...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

